import {
  AccordionItemRecord,
  ArticleRecord,
  AuthorRecord,
  CareerRecord,
  ExpertiseRecord,
  HomepageRecord,
  JobListingLayoutRecord,
  OfficeLocationRecord,
  OfficeRecord,
  ReviewerRecord,
  ServiceRecord,
} from "@graphql/types/graphql";
import { ExtendedJobRecord, IDatoGeneralMeta, ModelTypes } from "@types";
import { getSiteUrlForLocale, Locale } from "consts/locale";
import { render } from "datocms-structured-text-to-plain-text";
import type {
  Article,
  BreadcrumbList,
  FAQPage,
  JobPosting,
  Organization,
  Person,
  ProfilePage,
  Service,
  WebSite,
  WithContext,
} from "schema-dts";
import { getBasePathForType, getLink, getTranslatedHref } from "utils/link";

const PIA_SAME_AS = [
  "https://www.facebook.com/www.pia.be/",
  "https://www.instagram.com/group_pia/",
  "https://www.linkedin.com/company/pia-group-your-financial-expert/",
  "https://www.tiktok.com/@pia_group",
] as const;

const htmlToPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const richTextToPlainText = (value: unknown): string => {
  if (typeof value === "string") return value.trim();
  if (value == null || typeof value !== "object") return "";
  const obj = value as Record<string, unknown>;
  if (typeof obj.value === "string") return obj.value.trim();
  if (obj.document && typeof obj.document === "object") {
    return richTextToPlainText(obj.document);
  }
  const children = Array.isArray(obj.children) ? obj.children : [];
  return children
    .map((child) => richTextToPlainText(child))
    .filter(Boolean)
    .join(" ")
    .trim();
};

const toAbsoluteUrl = (siteUrl: string, src?: string | null) => {
  if (!src) return undefined;
  return src.startsWith("http") ? src : `${siteUrl}${src}`;
};

const getRecordUrl = (record: { __typename?: string; slug?: string }, locale?: Locale) => {
  if (!locale) return "";
  return `${getSiteUrlForLocale(locale)}${getTranslatedHref(getLink(record, locale), locale)}`;
};

const microData = (id: string, json: any) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify(json).replace(/</g, "\\u003c"),
    }}
    id={id}
  />
);

export const generateBreadcrumbsMicroData = (items: { name: string; item?: string }[]) => {
  if (!items || items.length === 0) return null;

  return microData("breadcrumbs", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      ...(item.item ? { item: item.item } : {}),
    })),
  } as WithContext<BreadcrumbList>);
};

export const generateFaqMicroData = (items: AccordionItemRecord[]) => {
  if (!items || items.length === 0) return null;

  const mainEntity = items
    .filter((item) => item?.title)
    .map((item) => ({
      "@type": "Question",
      name: item.title,
      acceptedAnswer: {
        "@type": "Answer",
        text: render(item.content as any),
      },
    }));

  if (mainEntity.length === 0) return null;

  return microData("faq-page", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity,
  } as WithContext<FAQPage>);
};

const buildArticlePerson = (
  person: AuthorRecord | ReviewerRecord,
  articleUrl: string,
  siteUrl: string,
  locale: Locale | undefined,
  role: "author" | "reviewer",
): WithContext<Person> => {
  const personUrl = getRecordUrl(person, locale);
  const imageUrl = toAbsoluteUrl(siteUrl, person.profilePicture?.responsiveImage?.src);

  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${articleUrl}#${role}`,
    name: person.name,
    ...(personUrl && { url: personUrl }),
    ...(person.functionTitle && { jobTitle: person.functionTitle }),
    ...(imageUrl && { image: imageUrl }),
  };
};

const buildProfilePageData = (
  person: AuthorRecord | ReviewerRecord,
  locale: Locale | undefined,
  siteUrl: string,
): WithContext<ProfilePage> => {
  const pageUrl = getRecordUrl(person, locale);
  const personId = `${pageUrl}#person`;
  const imageUrl = toAbsoluteUrl(siteUrl, person.profilePicture?.responsiveImage?.src);
  const introduction = richTextToPlainText(person.introduction?.value);

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": pageUrl,
    url: pageUrl,
    ...(person._createdAt && { dateCreated: person._createdAt }),
    ...(person._updatedAt && { dateModified: person._updatedAt }),
    mainEntity: {
      "@type": "Person",
      "@id": personId,
      name: person.name,
      alternateName: person.slug,
      identifier: person.id,
      url: pageUrl,
      ...(introduction && { description: introduction }),
      ...(person.functionTitle && { jobTitle: person.functionTitle }),
      ...(imageUrl && { image: imageUrl }),
    },
  };
};

export const generateSiteMicroData = (
  fields:
    | ServiceRecord
    | ExpertiseRecord
    | ArticleRecord
    | AuthorRecord
    | ReviewerRecord
    | CareerRecord
    | HomepageRecord
    | JobListingLayoutRecord
    | OfficeRecord,
  general?: IDatoGeneralMeta,
  locale?: Locale,
) => {
  const siteUrl = getSiteUrlForLocale(locale);

  switch (fields.__typename) {
    case ModelTypes.HomepageRecord:
      return (
        <>
          {microData("org", {
            "@context": "https://schema.org",
            "@type": "Organization",
            "@id": `${siteUrl}/#organization`,
            name: "PIA Group",
            url: siteUrl,
            email: general?.email,
            telephone: general?.phone || undefined,
            sameAs: [...PIA_SAME_AS],
            address: {
              "@type": "PostalAddress",
              streetAddress: general?.addressStreet,
              addressLocality: general?.addressCity,
              postalCode: general?.addressPostcode,
              addressCountry: general?.addressCountry,
            },
            vatID: general?.vat,
          } as WithContext<Organization>)}
          {microData("website", {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${siteUrl}/#website`,
            name: "PIA Group",
            url: siteUrl,
            inLanguage: ["nl-BE", "fr-BE", "en"],
            sameAs: [...PIA_SAME_AS],
            potentialAction: {
              "@type": "SearchAction",
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${siteUrl}/nl/search?term={search_term_string}`,
              },
              "query-input": "required name=search_term_string",
            },
            publisher: {
              "@type": "Organization",
              name: "PIA Group",
            },
          } as WithContext<WebSite>)}
        </>
      );
    case ModelTypes.ServiceRecord:
      return microData("service", {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: fields.title,
        description: render(fields.intro as any),
        provider: {
          "@type": "Organization",
          name: "PIA Group",
        },
        areaServed: {
          "@type": "GeoCircle",
          geoMidpoint: {
            "@type": "GeoCoordinates",
            latitude: general?.geoMidpoint?.latitude,
            longitude: general?.geoMidpoint?.longitude,
          },
          geoRadius: 200000,
        },
      } as WithContext<Service>);
    case ModelTypes.ExpertiseRecord:
      return microData("service", {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: fields.title,
        description: render(fields.intro as any),
        provider: {
          "@type": "Organization",
          name: "PIA Group",
        },
        areaServed: {
          "@type": "GeoCircle",
          geoMidpoint: {
            "@type": "GeoCoordinates",
            latitude: general?.geoMidpoint?.latitude,
            longitude: general?.geoMidpoint?.longitude,
          },
          geoRadius: 200000,
        },
      } as WithContext<Service>);
    case ModelTypes.AuthorRecord:
      return microData(
        "author-profile",
        buildProfilePageData(fields as AuthorRecord, locale, siteUrl),
      );
    case ModelTypes.ReviewerRecord:
      return microData(
        "reviewer-profile",
        buildProfilePageData(fields as ReviewerRecord, locale, siteUrl),
      );
    case ModelTypes.ArticleRecord: {
      const article = fields as ArticleRecord;
      const articleUrl = getRecordUrl(article, locale);
      const articleKeywords = [
        article.tag?.title,
        ...(article.secondaryTags?.map((t) => t.title).filter(Boolean) ?? []),
      ]
        .filter(Boolean)
        .join(", ");

      const persons: WithContext<Person>[] = [];
      const authorReferences: { "@id": string }[] = [];

      if (article.author) {
        persons.push(buildArticlePerson(article.author, articleUrl, siteUrl, locale, "author"));
        authorReferences.push({ "@id": `${articleUrl}#author` });
      }
      if (article.reviewer) {
        persons.push(buildArticlePerson(article.reviewer, articleUrl, siteUrl, locale, "reviewer"));
        authorReferences.push({ "@id": `${articleUrl}#reviewer` });
      }

      const articleNode = {
        "@type": "Article",
        name: article.title,
        description: richTextToPlainText(article.shortIntro.value),
        image: article.introImage?.responsiveImage?.src,
        datePublished: article.date,
        ...(article._updatedAt && { dateModified: article._updatedAt }),
        publisher: {
          "@id": `${siteUrl}/#organization`,
        },
        articleSection: article.tag.title,
        ...(articleKeywords && { keywords: articleKeywords }),
        ...(authorReferences.length > 0 && {
          author: authorReferences.length === 1 ? authorReferences[0] : authorReferences,
        }),
      };

      if (persons.length > 0) {
        return microData("article", {
          "@context": "https://schema.org",
          "@graph": [...persons, articleNode],
        });
      }

      return microData("article", {
        "@context": "https://schema.org",
        ...articleNode,
      } as WithContext<Article>);
    }
    case "JobListingLayoutRecord": {
      const data = fields as ExtendedJobRecord;
      const categories = data.categories?.map((x) => x.name).join(", ");
      const advantages = data.advantages?.map((x) => x.name).join(", ");
      const regions = data.place?.regions?.map((x) => x.name).join(", ");
      const workSchedules = data.workschedules?.map((x) => x.name).join(", ");

      const descriptionFromIntro =
        typeof data.intro === "string" && data.intro.trim() ? htmlToPlainText(data.intro) : "";
      const descriptionFromShortIntro =
        typeof data.shortIntro === "string" && data.shortIntro.trim()
          ? htmlToPlainText(data.shortIntro)
          : "";
      const jobPostingDescription =
        descriptionFromIntro || descriptionFromShortIntro || undefined;

      return microData("job-posting", {
        "@context": "https://schema.org",
        "@type": "JobPosting",
        title: data.title,
        datePosted: data.publicationDate,
        description: jobPostingDescription,
        jobBenefits: advantages,
        industry: categories,
        hiringOrganization: {
          "@type": "Organization",
          name: "PIA Group",
        },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: data.place?.address?.city,
            postalCode: data.place?.address?.postalCode,
            addressCountry: data.place?.country,
            addressRegion: regions,
          },
        },
        occupationalCategory: categories,
        salaryCurrency: "EUR",
        workHours: workSchedules,
        employmentType: workSchedules,
      } as WithContext<JobPosting>);
    }
    case ModelTypes.OfficeRecord: {
      const office = fields as OfficeRecord;
      const link = getLink(office, locale);
      const officePageUrl = `${siteUrl}${getBasePathForType(office.__typename ?? "OfficeRecord")}${
        link.slug ?? ""
      }`;

      const locations = office.officeLocations?.length
        ? office.officeLocations
        : ([
            {
              id: office.id,
              title: office.title,
              slug: office.slug,
              officeAddress: null,
              phone: null,
              email: null,
            },
          ] as Partial<OfficeLocationRecord>[]);

      return locations.map((location, index) => {
        const addr = location.officeAddress as
          | {
              street?: string;
              housenumber?: string;
              city?: string;
              postcode?: string;
              country?: string;
              latitude?: number;
              longitude?: number;
              display_name?: string;
            }
          | null
          | undefined;

        const hasMap = addr?.display_name
          ? `https://www.google.com/maps/place/${encodeURIComponent(addr.display_name)}`
          : undefined;

        const streetAddress =
          addr?.street && addr?.housenumber
            ? `${addr.street} ${addr.housenumber}`
            : addr?.street ?? "";

        const googleBusinessUrl = office.googleBusinessUrl;

        return microData(`local-business-${location.slug ?? index}`, {
          "@context": "https://schema.org",
          "@type": "AccountingService",
          "@id":
            locations.length > 1
              ? `${officePageUrl}#location-${location.slug ?? index}`
              : `${officePageUrl}#accounting-service`,
          name:
            locations.length > 1
              ? `${office.title} - ${location.title ?? office.title}`
              : office.title,
          url: officePageUrl,
          parentOrganization: {
            "@id": `${siteUrl}/#organization`,
          },
          address: {
            "@type": "PostalAddress",
            streetAddress,
            addressLocality: addr?.city ?? "",
            postalCode: addr?.postcode ?? "",
            addressCountry: addr?.country ?? "",
          },
          ...(addr?.latitude != null &&
            addr?.longitude != null && {
              geo: {
                "@type": "GeoCoordinates",
                latitude: addr.latitude,
                longitude: addr.longitude,
              },
            }),
          ...(hasMap && { hasMap }),
          ...(location.phone && { telephone: location.phone }),
          ...(location.email && { email: location.email }),
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "09:00",
              closes: "18:00",
            },
          ],
          areaServed: {
            "@type": addr?.city ? "City" : "Country",
            name: addr?.city ?? addr?.country ?? "BE",
          },
          makesOffer: {
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: "Accountancy and tax advisory",
            },
          },
          ...(googleBusinessUrl && { sameAs: [googleBusinessUrl] }),
        });
      });
    }
    default:
  }
};
